import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import ImportarRazaoService, {
  ContaPendente,
  EventoOpcao,
} from "../../../services/ImportarRazaoService";

const brl = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const normalizar = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Natureza pelo 1º dígito da conta (mesma regra do alerta de conta virada, DEVN3-528). */
type Natureza = "Ativo" | "Passivo" | "Resultado";
const naturezaDe = (codigo?: string | null): Natureza | null => {
  const d = (codigo || "").replace(/\D/g, "")[0];
  if (d === "1") return "Ativo";
  if (d === "2") return "Passivo";
  if (d && d >= "3") return "Resultado";
  return null;
};

const CATEGORIAS = [
  "Banco", "Caixa", "Fornecedor", "Cliente", "Tributo", "Salário",
  "Imobilizado", "Empréstimo", "Adiantamento", "Investimento",
];

interface Props {
  esteiraId: number;
  empresaNome?: string;
  periodo?: string;
  /** Chamado quando a importação é concluída com sucesso (card vai para "Concluído"). */
  onConcluido: () => void;
  /** Chamado após vincular contas — o board deve recarregar para refletir a contagem. */
  onClassificou?: () => void;
  /** Abre o log da importação (quando o upload da esteira é conhecido). */
  onVerLog?: () => void;
  onClose: () => void;
}

/**
 * Modal de mapeamento de contas (DEVN3-540; tela cheia e lista de eventos com busca/filtros na DEVN3-602).
 * À esquerda as contas importadas sem conta referencial; à direita os eventos — busca por nome ou código,
 * filtro por categoria e natureza, e os da mesma natureza das contas selecionadas primeiro ("Sugerido").
 */
const ModalMapeamento = ({ esteiraId, empresaNome, periodo, onConcluido, onClassificou, onVerLog, onClose }: Props) => {
  const [contas, setContas] = useState<ContaPendente[] | null>(null);
  const [eventos, setEventos] = useState<EventoOpcao[]>([]);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [classificando, setClassificando] = useState(false);
  const [concluindo, setConcluindo] = useState(false);

  const [buscaConta, setBuscaConta] = useState("");
  const [buscaEvento, setBuscaEvento] = useState("");
  const [categoria, setCategoria] = useState<string | null>(null);
  const [natureza, setNatureza] = useState<Natureza | null>(null);

  const carregar = useCallback(async () => {
    const [cs, evs] = await Promise.all([
      ImportarRazaoService.contasPendentes(esteiraId),
      ImportarRazaoService.eventos(),
    ]);
    setContas(cs);
    setEventos(evs);
    setSelecionadas(new Set());
  }, [esteiraId]);

  useEffect(() => {
    carregar().catch(() =>
      Swal.fire({ icon: "error", text: "Não foi possível carregar as contas da importação." })
    );
  }, [carregar]);

  // Esc fecha
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const contasVisiveis = useMemo(() => {
    const q = normalizar(buscaConta.trim());
    return (contas ?? []).filter(
      (c) => !q || normalizar(`${c.contaEmpresa} ${c.descricao ?? ""}`).includes(q)
    );
  }, [contas, buscaConta]);

  /** Natureza das contas selecionadas (se todas iguais) — ordena os eventos sugeridos primeiro. */
  const naturezaSelecao = useMemo(() => {
    const ns = new Set(Array.from(selecionadas).map((c) => naturezaDe(c)));
    return ns.size === 1 ? Array.from(ns)[0] : null;
  }, [selecionadas]);

  const eventosVisiveis = useMemo(() => {
    const q = normalizar(buscaEvento.trim());
    const lista = eventos.filter((ev) => {
      if (q && !normalizar(`${ev.nome ?? ""} ${ev.contaReferencial}`).includes(q)) return false;
      if (categoria && !(ev.categorias ?? []).includes(categoria)) return false;
      if (natureza && naturezaDe(ev.contaReferencial) !== natureza) return false;
      return true;
    });
    if (!naturezaSelecao) return lista;
    const sugeridos = lista.filter((ev) => naturezaDe(ev.contaReferencial) === naturezaSelecao);
    const outros = lista.filter((ev) => naturezaDe(ev.contaReferencial) !== naturezaSelecao);
    return [...sugeridos, ...outros];
  }, [eventos, buscaEvento, categoria, natureza, naturezaSelecao]);

  const eventoSelecionado = eventos.find((e) => e.id === eventoId) || null;

  function toggleConta(conta: string) {
    setSelecionadas((prev) => {
      const s = new Set(Array.from(prev));
      if (s.has(conta)) s.delete(conta);
      else s.add(conta);
      return s;
    });
  }

  const todasVisiveisMarcadas =
    contasVisiveis.length > 0 && contasVisiveis.every((c) => selecionadas.has(c.contaEmpresa));

  function toggleTodasVisiveis() {
    setSelecionadas((prev) => {
      const s = new Set(Array.from(prev));
      if (todasVisiveisMarcadas) contasVisiveis.forEach((c) => s.delete(c.contaEmpresa));
      else contasVisiveis.forEach((c) => s.add(c.contaEmpresa));
      return s;
    });
  }

  async function vincular() {
    if (!eventoId || selecionadas.size === 0) {
      Swal.fire({ icon: "warning", text: "Selecione ao menos uma conta e um evento." });
      return;
    }
    setClassificando(true);
    try {
      const r = await ImportarRazaoService.classificar(esteiraId, Array.from(selecionadas), eventoId);
      await carregar();
      setEventoId(null);
      onClassificou?.();
      Swal.fire({
        icon: "success",
        text: `${r.classificadas} conta(s) vinculada(s). Faltam ${r.pendentes}.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Falha ao classificar." });
    } finally {
      setClassificando(false);
    }
  }

  async function concluir() {
    setConcluindo(true);
    try {
      const r = await ImportarRazaoService.concluir(esteiraId);
      if (r.concluida) {
        await Swal.fire({
          icon: "success",
          title: "Importação concluída",
          text: "O razão foi importado e todas as contas classificadas.",
          timer: 2500,
          showConfirmButton: false,
        });
        onConcluido();
      } else {
        Swal.fire({
          icon: "warning",
          text: r.mensagem || `Ainda há ${r.pendentes} conta(s) pendente(s).`,
        });
      }
    } finally {
      setConcluindo(false);
    }
  }

  const totalContas = contas?.length ?? 0;
  const chip = (ativo: boolean) => ({
    fontSize: 11, padding: "3px 10px", borderRadius: 999, cursor: "pointer",
    border: `1px solid ${ativo ? "#0EA5E9" : "#E2E8F0"}`,
    background: ativo ? "#E0F2FE" : "#fff", color: ativo ? "#075985" : "#475569", fontWeight: ativo ? 700 : 500,
  });

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.4)", zIndex: 9999,
        padding: 16, backdropFilter: "blur(4px)",
      }}
    >
      <div
        className="card shadow-lg"
        style={{ width: "100%", height: "100%", overflow: "hidden", borderRadius: 16, border: "none", display: "flex", flexDirection: "column" }}
      >
        <div className="card-header d-flex justify-content-between align-items-center py-4 px-5" style={{ background: "#F8FAFC", borderRadius: "16px 16px 0 0" }}>
          <div>
            <h3 className="card-title fw-bolder text-dark m-0" style={{ fontSize: 18 }}>Mapeamento de contas</h3>
            <div className="text-muted fs-7">
              {empresaNome}
              {periodo ? ` · ${periodo}` : ""}
              {contas !== null && ` · ${totalContas} conta(s) pendente(s)`}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {onVerLog && (
              <button className="btn btn-sm btn-light" onClick={onVerLog}>
                <i className="bi bi-journal-text me-1" />Ver log da importação
              </button>
            )}
            <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={onClose} title="Fechar (Esc)">
              <i className="bi bi-x fs-2" />
            </button>
          </div>
        </div>

        <div className="card-body p-0" style={{ flex: 1, minHeight: 0, display: "flex" }}>
          {contas === null ? (
            <div className="text-center text-muted py-8 w-100">Carregando contas...</div>
          ) : totalContas === 0 ? (
            <div className="alert alert-success m-5 w-100">
              Todas as contas já estão classificadas. Você pode concluir a importação.
            </div>
          ) : (
            <>
              {/* ── Contas ─────────────────────────────────────────── */}
              <div style={{ flex: 3, minWidth: 0, display: "flex", flexDirection: "column", borderRight: "1px solid #E4E6EF" }}>
                <div className="p-4 d-flex gap-3 align-items-center" style={{ borderBottom: "1px solid #F1F5F9" }}>
                  <input className="form-control form-control-sm" placeholder="Buscar conta por código ou descrição..."
                    value={buscaConta} onChange={(e) => setBuscaConta(e.target.value)} />
                  <span className="text-muted text-nowrap" style={{ fontSize: 12 }}>
                    {selecionadas.size} selecionada(s)
                  </span>
                </div>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  <table className="table table-row-bordered align-middle mb-0" style={{ fontSize: 12 }}>
                    <thead className="sticky-top bg-white">
                      <tr className="fw-bold text-muted">
                        <th style={{ width: 40, paddingLeft: 16 }}>
                          <input type="checkbox" checked={todasVisiveisMarcadas} onChange={toggleTodasVisiveis}
                            title="Selecionar todas as visíveis" />
                        </th>
                        <th>Conta</th>
                        <th>Descrição</th>
                        <th className="text-end">Débito</th>
                        <th className="text-end">Crédito</th>
                        <th className="text-end" style={{ paddingRight: 16 }}>Saldo final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contasVisiveis.map((c) => {
                        const marcada = selecionadas.has(c.contaEmpresa);
                        return (
                          <tr key={c.contaEmpresa} onClick={() => toggleConta(c.contaEmpresa)}
                            style={{ cursor: "pointer", background: marcada ? "#F0F9FF" : undefined }}>
                            <td style={{ paddingLeft: 16 }}>
                              <input type="checkbox" checked={marcada} onChange={() => toggleConta(c.contaEmpresa)}
                                onClick={(e) => e.stopPropagation()} />
                            </td>
                            <td className="font-monospace">{c.contaEmpresa}</td>
                            <td>{c.descricao}</td>
                            <td className="text-end">{brl(c.totalDebito)}</td>
                            <td className="text-end">{brl(c.totalCredito)}</td>
                            <td className="text-end" style={{ paddingRight: 16 }}>{brl(c.saldoFinal)}</td>
                          </tr>
                        );
                      })}
                      {contasVisiveis.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted py-6">Nenhuma conta encontrada.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Eventos ────────────────────────────────────────── */}
              <div style={{ flex: 2, minWidth: 320, display: "flex", flexDirection: "column" }}>
                <div className="p-4" style={{ borderBottom: "1px solid #F1F5F9" }}>
                  <input className="form-control form-control-sm mb-3" placeholder="Buscar evento por nome ou conta referencial..."
                    value={buscaEvento} onChange={(e) => setBuscaEvento(e.target.value)} />
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {(["Ativo", "Passivo", "Resultado"] as Natureza[]).map((n) => (
                      <span key={n} style={chip(natureza === n)} onClick={() => setNatureza(natureza === n ? null : n)}>{n}</span>
                    ))}
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {CATEGORIAS.filter((cat) => eventos.some((ev) => (ev.categorias ?? []).includes(cat))).map((cat) => (
                      <span key={cat} style={chip(categoria === cat)} onClick={() => setCategoria(categoria === cat ? null : cat)}>{cat}</span>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {eventosVisiveis.map((ev) => {
                    const escolhido = ev.id === eventoId;
                    const sugerido = !!naturezaSelecao && naturezaDe(ev.contaReferencial) === naturezaSelecao;
                    return (
                      <div key={ev.id} onClick={() => setEventoId(escolhido ? null : ev.id)}
                        style={{
                          padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid #F1F5F9",
                          background: escolhido ? "#E0F2FE" : undefined,
                          borderLeft: `3px solid ${escolhido ? "#0EA5E9" : "transparent"}`,
                        }}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>{ev.nome || "(sem nome)"}</span>
                          {sugerido && (
                            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#DCFCE7", color: "#166534", fontWeight: 700 }}>
                              SUGERIDO
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                          <span className="font-monospace" style={{ fontSize: 11, color: "#64748B" }}>{ev.contaReferencial}</span>
                          {(ev.categorias ?? []).map((cat) => (
                            <span key={cat} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: "#F1F5F9", color: "#475569" }}>{cat}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {eventosVisiveis.length === 0 && (
                    <div className="text-center text-muted py-6" style={{ fontSize: 12 }}>Nenhum evento encontrado.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="card-footer d-flex justify-content-between align-items-center gap-3 py-4 px-5 bg-light-soft" style={{ borderRadius: "0 0 16px 16px" }}>
          <button className="btn btn-sm btn-secondary px-4" onClick={onClose}>Fechar</button>
          <div className="d-flex align-items-center gap-3">
            {totalContas > 0 && (
              <span className="text-muted text-truncate" style={{ fontSize: 12, maxWidth: 520 }}>
                {eventoSelecionado
                  ? `Evento: ${eventoSelecionado.nome ?? ""} (${eventoSelecionado.contaReferencial})`
                  : "Selecione as contas à esquerda e um evento à direita."}
              </span>
            )}
            {totalContas > 0 && (
              <button className="btn btn-sm btn-primary px-4" onClick={vincular}
                disabled={classificando || selecionadas.size === 0 || !eventoId}>
                {classificando ? "Vinculando..." : `Vincular ${selecionadas.size} conta(s)`}
              </button>
            )}
            <button className="btn btn-sm btn-success px-4" onClick={concluir}
              disabled={concluindo || contas === null || totalContas > 0}>
              {concluindo ? "Concluindo..." : "Concluir importação"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMapeamento;
